import { create } from 'zustand'
import type { Role, UserProfile } from '@insightai/api-client'

interface AuthState {
  /**
   * SEC-FR-001 — IN MEMORY ONLY.
   *
   * Never persisted, never mirrored to localStorage/sessionStorage/IndexedDB, and
   * deliberately NOT using zustand's `persist` middleware. The AC test notes
   * probe web storage for this value, and the ESLint `no-restricted-globals` rule
   * blocks the accidental path. Losing the token on reload is correct behaviour:
   * the HttpOnly refresh cookie restores the session.
   */
  accessToken: string | null
  /** Epoch ms when the access token expires; drives proactive refresh. */
  expiresAt: number | null
  user: UserProfile | null
  activeWorkspaceId: string | null
  status: 'unknown' | 'authenticated' | 'anonymous'

  setSession: (token: string, expiresInSeconds: number) => void
  setUser: (user: UserProfile) => void
  setActiveWorkspace: (workspaceId: string) => void
  clear: () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  expiresAt: null,
  user: null,
  activeWorkspaceId: null,
  status: 'unknown',

  setSession: (token, expiresInSeconds) =>
    set({ accessToken: token, expiresAt: Date.now() + expiresInSeconds * 1000 }),

  setUser: (user) =>
    set((state) => ({
      user,
      status: 'authenticated',
      // Default to the first workspace, but never clobber an explicit choice.
      activeWorkspaceId: state.activeWorkspaceId ?? user.workspaces[0]?.id ?? null,
    })),

  setActiveWorkspace: (workspaceId) => set({ activeWorkspaceId: workspaceId }),

  clear: () =>
    set({
      accessToken: null,
      expiresAt: null,
      user: null,
      activeWorkspaceId: null,
      status: 'anonymous',
    }),
}))

/** Non-reactive read for the API client middleware, which is outside React. */
export const getAccessToken = () => useAuthStore.getState().accessToken

/** True when the token is absent or within `skewSeconds` of expiry. */
export function needsRefresh(skewSeconds = 60) {
  const { accessToken, expiresAt } = useAuthStore.getState()
  if (!accessToken || !expiresAt) return true
  return Date.now() >= expiresAt - skewSeconds * 1000
}

/**
 * AC-53 — RBAC helpers.
 *
 * These drive *affordances* only. The server is the authorisation boundary:
 * hiding a button is UX, a 403 is security. Never gate a mutation on these alone.
 */
export const ROLE_RANK: Record<Role, number> = {
  CLIENT_VIEW_ONLY: 10,
  CLIENT_EDITOR: 20,
  RESEARCH_OPS: 30,
  RESEARCH_LEAD: 40,
  WORKSPACE_ADMIN: 50,
  PLATFORM_ADMIN: 60,
}

export function hasRole(roles: Role[] | undefined, required: Role) {
  return Boolean(roles?.includes(required))
}

/** True when any held role ranks at or above `minimum`. */
export function hasAtLeast(roles: Role[] | undefined, minimum: Role) {
  if (!roles?.length) return false
  const floor = ROLE_RANK[minimum]
  return roles.some((role) => ROLE_RANK[role] >= floor)
}
